import _snakeCase from 'lodash/snakeCase';
import _camelCase from 'lodash/camelCase';
import _isPlainObject from 'lodash/isPlainObject';

/* eslint-disable @typescript-eslint/no-explicit-any */

interface PlainObject {
  [key: string]: any;
}
type PlainObjectEntry = [string, any];
type PlainObjectReducer = (
  acc: PlainObject,
  entry: PlainObjectEntry,
) => PlainObject;
type PlainObjectReducerFactory = (
  transform: (node: any) => any,
) => PlainObjectReducer;

const isPlainObject = (obj: any): obj is PlainObject => _isPlainObject(obj);

/**
 * Transform the key of a single entry to snake/camel case and recurse for the
 * value.
 *
 * @param keyTransformer snake/camel case string transformer
 */
const transformKeyReducerFactory = (
  keyTransformer: (key: string) => string,
): PlainObjectReducerFactory => (transform) => (acc, [key, value]) => {
  const transformedKey = keyTransformer(key);
  return { ...acc, [transformedKey]: transform(value) };
};

/**
 * Skip properties that have null values (i.e. treat them as undefined).
 */
const removeNullReducerFactory = (): PlainObjectReducerFactory => (
  transform,
) => (acc, [key, value]) => {
  if (value === null) return acc;
  return { ...acc, [key]: transform(value) };
};

/**
 * Return a recursive transformer function that transforms all keys of an object
 * or array of objects to snake/camel case.
 *
 * @param getObjectReducer function that returns an object reducer
 */
const createTransformer = (getObjectReducer: PlainObjectReducerFactory) => {
  return function transform(node: any): any {
    if (Array.isArray(node)) {
      return node.map(transform);
    }

    if (isPlainObject(node)) {
      // Use .keys.map instead of .entries for IE compatibility.
      return Object.keys(node)
        .map<PlainObjectEntry>((key) => [key, node[key]])
        .reduce<PlainObject>(getObjectReducer(transform), {});
    }

    return node;
  };
};

export const camelCase = createTransformer(
  transformKeyReducerFactory(_camelCase),
);
export const snakeCase = createTransformer(
  transformKeyReducerFactory(_snakeCase),
);
export const removeNull = createTransformer(removeNullReducerFactory());
